import { useRef } from "react"
import { DomainUser, getAdminUserList, putAdminUserStatus, putAdminUserSetAdmin } from "@/api/platform"
import type { ProColumns, ActionType } from '@ant-design/pro-components'
import { ProTable } from '@ant-design/pro-components'
import dayjs from "dayjs"
import { Space, Typography, Tag, Button, message, Modal } from "antd"
import { GithubOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import Avatar from "antd/es/avatar"
import { useRequest } from 'ahooks'
import axios from 'axios'

const GoogleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
)

interface UserRecord extends DomainUser {
  status?: number
  marketing_source?: { id: number; code: string; name: string; source_type: string }
  marketing_source_at?: number
  marketing_source_bind_type?: 'new' | 'reactivation'
}

const statusMap: Record<number, { color: string; text: string }> = {
  1: { color: 'green', text: '启用' },
  2: { color: 'red', text: '禁用' },
}

const UserList = () => {
  const actionRef = useRef<ActionType>()
  const navigate = useNavigate()
  const { data: sourceList = [] } = useRequest(() => axios.get('/api/v1/admin/marketing-sources', { withCredentials: true }).then((res) => res.data.data || []))

  const handleSetAdmin = (record: UserRecord) => {
    Modal.confirm({
      title: '设为管理员',
      content: `确定将用户 ${record.name || record.email} 设为管理员吗？该用户将获得超级管理员权限。`,
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        try {
          await putAdminUserSetAdmin(record.id!)
          message.success('已设为管理员')
          actionRef.current?.reload()
        } catch {
          message.error('操作失败')
        }
      },
    })
  }

  const handleToggleStatus = (record: UserRecord, newStatus: number) => {
    const actionText = newStatus === 1 ? '启用' : '禁用'
    Modal.confirm({
      title: `确认${actionText}`,
      content: `确定要${actionText}用户 ${record.name || record.email} 吗？`,
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        try {
          await putAdminUserStatus(record.id!, { status: newStatus })
          message.success(`${actionText}成功`)
          actionRef.current?.reload()
        } catch (error) {
          message.error(`${actionText}失败`)
        }
      },
    })
  }

  const columns: ProColumns<UserRecord>[] = [
    {
      title: '用户 ID',
      dataIndex: 'id',
      width: 180,
      search: {
        transform: (value) => {
          return {
            id: value,
          }
        },
      },
      render(dom, entity, index, action, schema) {
        return <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Avatar size={24} src={entity.avatar} />
          <Typography.Text copyable>{entity.id}</Typography.Text>
        </div>
      },
    },
    {
      title: '昵称',
      dataIndex: 'name',
      width: 140,
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      width: 180,
      render(dom, entity, index, action, schema) {
        return (
          <Space direction="vertical" size="small">
            <span>{entity.email}</span>
            {entity.github && (
              <Space align="center">
                <GithubOutlined style={{ fontSize: '16px', color: '#1890ff' }} />
                <span>{entity.github.email}</span>
              </Space>
            )}
            {entity.google && (
              <Space align="center">
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <GoogleIcon />
                </div>
                <span>{entity.google.email}</span>
              </Space>
            )}
          </Space>
        )
      },
    },
    {
	  title: '来源信息', key: 'marketing_source', width: 260, search: false,
	  render: (_, record) => {
		if (!record.marketing_source) return '-'
		const type = record.marketing_source.source_type === 'Media' ? '媒体' : record.marketing_source.source_type
		const bindType = record.marketing_source_bind_type === 'new' ? '新用户' : record.marketing_source_bind_type === 'reactivation' ? '召回' : '-'
		return <Space direction='vertical' size={2}>
		  <Space size={6}><Typography.Text>{record.marketing_source.name}</Typography.Text><Tag>{type}</Tag></Space>
		  <Typography.Text type='secondary' style={{ fontSize: 12 }}>{bindType} · {record.marketing_source_at ? dayjs.unix(record.marketing_source_at).format('YYYY-MM-DD HH:mm:ss') : '-'}</Typography.Text>
		</Space>
	  },
    },
    {
	  title: '来源', dataIndex: 'marketing_source_id', hideInTable: true, valueType: 'select', fieldProps: { showSearch: true, optionFilterProp: 'label', options: sourceList.map((source: any) => ({ label: `${source.name} (${source.code})`, value: source.id })) },
    },
    {
	  title: '来源类型', dataIndex: 'marketing_source_type', hideInTable: true, valueType: 'select', valueEnum: { KOL: { text: 'KOL' }, Media: { text: '媒体' } },
    },
    {
	  title: '来源方式', dataIndex: 'marketing_source_bind_type', hideInTable: true, valueType: 'select', valueEnum: { new: { text: '新用户' }, reactivation: { text: '召回' } },
    },
    {
	  title: '来源时间', dataIndex: 'marketing_source_at', hideInTable: true, valueType: 'dateRange',
	  search: { transform: (value) => ({ marketing_source_start_at: dayjs(value[0]).unix(), marketing_source_end_at: dayjs(value[1]).unix() }) },
    },
    {
	  title: '是否有来源', dataIndex: 'has_marketing_source', hideInTable: true, valueType: 'select',
	  valueEnum: { true: { text: '有来源' }, false: { text: '无来源' } },
	  search: { transform: (value) => ({ has_marketing_source: value === 'true' }) },
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      search: false,
      render: (_, record) => {
        const info = statusMap[record.status ?? 1]
        return <Tag color={info.color}>{info.text}</Tag>
      },
    },
    {
      title: '订单数',
      dataIndex: 'order_count',
      width: 180,
      search: false,
      render: (_, record) => (
        <Typography.Link
          onClick={() => {
            navigate(`/platform/order/list?userId=${record.id}`)
          }}
        >
          {record.order_count}
        </Typography.Link>
      ),
    },
    {
      title: '是否下单',
      width: 180,
      valueType: 'switch',
      hideInTable: true,
      search: {
        transform: (value) => {
          return {
            has_order: value,
          }
        },
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      width: 180,
      valueType: 'dateRange',
      render: (_, record) => {
        return dayjs.unix(record.created_at!).format('YYYY-MM-DD HH:mm:ss')
      },
      search: {
        transform: (value) => {
          return {
            start_at: dayjs(value[0]).unix(),
            end_at: dayjs(value[1]).unix(),
          }
        },
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 160,
      fixed: 'right',
      search: false,
      render: (_, record) => {
        const isDisabled = record.status === 2
        const isAdmin = record.is_admin
        return (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <Button
              type="link"
              size="small"
              danger={isDisabled}
              onClick={() => handleToggleStatus(record, isDisabled ? 1 : 2)}
            >
              {isDisabled ? '启用' : '禁用'}
            </Button>
            {!isAdmin && (
              <Button
                type="link"
                size="small"
                onClick={() => handleSetAdmin(record)}
              >
                设为管理员
              </Button>
            )}
          </div>
        )
      },
    },
  ]
  return (
    <>
      <ProTable<UserRecord>
        rowKey='id'
        columns={columns}
        actionRef={actionRef}
        pagination={{
          pageSize: 10,
        }}
        request={async (params) => {
          try {
            const res = await getAdminUserList({
              page: params.current!,
              size: params.pageSize,
              all: 1,
              id: params.id,
              name: params.name,
              email: params.email,
              start_at: params.start_at,
              end_at: params.end_at,
              has_order: params.has_order,
			  has_marketing_source: params.has_marketing_source,
			  marketing_source_id: params.marketing_source_id,
			  marketing_source_type: params.marketing_source_type,
			  marketing_source_bind_type: params.marketing_source_bind_type,
			  marketing_source_start_at: params.marketing_source_start_at,
			  marketing_source_end_at: params.marketing_source_end_at,
            })
            const result = {
              data: res?.users || [],
              total: res?.total_count || 0,
              success: true,
            }
            return result
          } catch (error) {
            console.error('请求错误:', error)
            return {
              data: [],
              total: 0,
              success: false,
            }
          }
        }}
      />
    </>
  )
}

export default UserList
